import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import Seo from "@/components/seo/Seo";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { SITE_INFO, articles, comics, categories, comments as mockComments } from "@/mocks/data";
import type { Comment } from "@/mocks/data";

const avatarColors = ["bg-primary-500", "bg-accent-500", "bg-secondary-500", "bg-primary-700", "bg-secondary-700", "bg-accent-700"];

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === id);

  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(article ? article.likes : 0);
  const [comments, setComments] = useState<Comment[]>(mockComments);
  const [commentText, setCommentText] = useState("");

  if (!article) {
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center px-4 text-center">
        <h1 className="font-heading text-2xl font-black text-foreground-950">未找到该文章</h1>
        <p className="mt-2 text-sm text-foreground-600">这篇文章可能已被删除或链接有误。</p>
        <Link to="/" className="mt-5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600">
          返回首页
        </Link>
      </div>
    );
  }

  const category = categories.find((c) => c.id === article.categoryId);
  const related = articles.filter((a) => a.id !== article.id && a.categoryId === article.categoryId).slice(0, 3);
  const fallbackRelated = related.length < 3 ? articles.filter((a) => a.id !== article.id && !related.some((r) => r.id === a.id)).slice(0, 3 - related.length) : [];

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: article.title,
      description: article.excerpt,
      datePublished: article.date,
      author: { "@type": "Person", name: article.author },
      publisher: { "@type": "Organization", name: SITE_INFO.name },
      image: article.cover,
      mainEntityOfPage: `${SITE_INFO.domain}/article/${article.id}`,
      articleSection: category?.name,
      inLanguage: "zh-CN",
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: `${SITE_INFO.domain}/` },
        { "@type": "ListItem", position: 2, name: category?.name || "分类", item: `${SITE_INFO.domain}/category/${article.categoryId}` },
        { "@type": "ListItem", position: 3, name: article.title, item: `${SITE_INFO.domain}/article/${article.id}` },
      ],
    },
  ];

  function handleLike() {
    setLiked((v) => !v);
    setLikeCount((c) => (liked ? c - 1 : c + 1));
  }

  function handleAddComment(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const text = commentText.trim();
    if (!text) return;
    const newComment: Comment = {
      id: `local-${Date.now()}`,
      userName: "热心同好",
      avatarText: "岛",
      avatarBg: avatarColors[Math.floor(Math.random() * avatarColors.length)],
      time: "刚刚",
      content: text,
      likes: 0,
      replies: 0,
    };
    setComments((prev) => [newComment, ...prev]);
    setCommentText("");
  }

  return (
    <>
      <Seo
        title={article.title}
        description={article.excerpt}
        keywords={`漫画岛,${article.title},${category?.name}书评,${article.comicRef}书评,漫画同好讨论`}
        canonicalPath={`/article/${article.id}`}
        jsonLd={jsonLd}
      />

      {/* 文章正文 */}
      <article className="mx-auto w-full max-w-4xl px-4 py-10 md:px-6 md:py-14">
        <Breadcrumb items={[{ label: "分类书库", to: `/category/${article.categoryId}` }, { label: category?.name || "分类", to: `/category/${article.categoryId}` }, { label: article.title }]} />

        <header className="mt-6">
          <div className="flex flex-wrap items-center gap-2 text-xs text-foreground-500">
            <Link to={`/category/${article.categoryId}`} className="rounded-full bg-primary-100 px-2.5 py-1 font-medium text-primary-800 hover:bg-primary-200">
              {category?.name}
            </Link>
            <span className="flex items-center gap-1"><i className="ri-eye-line" aria-hidden="true"></i>{(article.views / 1000).toFixed(1)}k 阅读</span>
            <span className="flex items-center gap-1"><i className="ri-chat-3-line" aria-hidden="true"></i>{article.comments} 评论</span>
          </div>
          <h1 className="mt-4 font-heading text-2xl font-black leading-tight text-foreground-950 md:text-4xl">{article.title}</h1>
          <p className="mt-4 max-w-3xl text-base leading-relaxed text-foreground-600 md:text-lg">{article.excerpt}</p>
          <div className="mt-5 flex flex-wrap items-center gap-4 text-sm text-foreground-500">
            <span className="flex items-center gap-2">
              <span className={`flex h-9 w-9 items-center justify-center rounded-full ${avatarColors[0]} text-sm font-bold text-background-50`}>{article.author.slice(0, 1)}</span>
              <span className="font-medium text-foreground-800">{article.author}</span>
              <span className="text-xs text-foreground-400">· {article.authorTitle}</span>
            </span>
            <span className="text-foreground-400">{article.date}</span>
            <span className="ml-auto flex items-center gap-2">
              <button
                type="button"
                onClick={handleLike}
                className={`flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-sm transition-colors ${
                  liked ? "border-primary-400 bg-primary-100 text-primary-700" : "border-background-300 text-foreground-600 hover:border-primary-300"
                }`}
                aria-pressed={liked}
              >
                <i className={`${liked ? "ri-heart-fill" : "ri-heart-line"}`} aria-hidden="true"></i>
                {(likeCount / 1000).toFixed(1)}k
              </button>
              <button type="button" className="flex items-center gap-1.5 rounded-full border border-background-300 px-3.5 py-1.5 text-sm text-foreground-600 transition-colors hover:border-primary-300" aria-label="分享">
                <i className="ri-share-forward-line" aria-hidden="true"></i> 分享
              </button>
            </span>
          </div>
        </header>

        <div className="mt-8 overflow-hidden rounded-lg border border-background-200">
          <img src={article.cover} alt={`${article.title}配图`} title={`${article.title}配图`} className="h-72 w-full object-cover object-top sm:h-96" />
        </div>

        <div className="mt-8 flex flex-col gap-6">
          {article.paragraphs.map((p, i) => (
            <p key={i} className="text-base leading-8 text-foreground-800">{p}</p>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap items-center gap-2">
          {article.tags.map((tag) => (
            <span key={tag} className="rounded-full bg-secondary-100 px-3 py-1 text-sm text-secondary-900">{tag}</span>
          ))}
        </div>

        {/* 作者卡片 */}
        <div className="mt-10 flex flex-col items-start gap-4 rounded-lg border border-background-200 bg-background-50 p-5 sm:flex-row sm:items-center">
          <span className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-full ${avatarColors[0]} text-xl font-bold text-background-50`}>
            {article.author.slice(0, 1)}
          </span>
          <div>
            <p className="font-heading text-base font-bold text-foreground-900">{article.author} · {article.authorTitle}</p>
            <p className="mt-1 text-sm text-foreground-600">
              在漫画岛持续分享{category?.name}方向的深度书评与安利内容，和 {SITE_INFO.members} 同好一起交流推荐。
            </p>
          </div>
          <Link to={`/category/${article.categoryId}`} className="ml-auto whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 hover:bg-primary-600 sm:ml-auto">
            关注 TA
          </Link>
        </div>

        {/* 相关推荐 */}
        <section className="mt-12" aria-label="相关书评推荐">
          <h2 className="flex items-center gap-2 font-heading text-xl font-black text-foreground-950">
            <i className="ri-magic-line text-primary-600" aria-hidden="true"></i> 相关推荐
          </h2>
          <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[...related, ...fallbackRelated].map((r) => (
              <Link key={r.id} to={`/article/${r.id}`} className="group overflow-hidden rounded-lg border border-background-200 bg-background-50">
                <div className="h-32 w-full overflow-hidden">
                  <img src={r.cover} alt={`${r.title}配图`} title={r.title} loading="lazy" className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105" />
                </div>
                <div className="p-4">
                  <h3 className="line-clamp-2 text-sm font-bold text-foreground-900 group-hover:text-primary-700">{r.title}</h3>
                  <p className="mt-2 flex items-center gap-3 text-xs text-foreground-500">
                    <span>{r.author}</span>
                    <span>{r.date}</span>
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* 评论区 / 粉丝圈子 */}
        <section className="mt-12" aria-label="评论区与粉丝圈子">
          <h2 className="flex items-center gap-2 font-heading text-xl font-black text-foreground-950">
            <i className="ri-chat-3-line text-primary-600" aria-hidden="true"></i> 粉丝圈子 · 评论区
            <span className="rounded-full bg-secondary-100 px-2.5 py-0.5 text-xs font-medium text-secondary-900">{comments.length}</span>
          </h2>

          <form onSubmit={handleAddComment} className="mt-5 flex flex-col gap-3 rounded-lg border border-background-200 bg-background-50 p-4">
            <label htmlFor="comment-input" className="sr-only">发表评论</label>
            <textarea
              id="comment-input"
              value={commentText}
              onChange={(e) => setCommentText(e.target.value.slice(0, 500))}
              maxLength={500}
              placeholder="看完漫画来聊剧情、求推荐，和大家一起讨论吧……（最多 500 字）"
              rows={3}
              className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
            />
            <div className="flex items-center justify-between">
              <span className="text-xs text-foreground-400">{commentText.length}/500</span>
              <button type="submit" className="whitespace-nowrap rounded-md bg-primary-500 px-5 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600">
                发表评论
              </button>
            </div>
          </form>

          <div className="mt-5 flex flex-col gap-4">
            {comments.map((c) => (
              <div key={c.id} className="flex gap-3 rounded-lg border border-background-200 bg-background-50 p-4">
                <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${c.avatarBg} text-sm font-bold text-background-50`}>
                  {c.avatarText}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2 text-sm">
                    <span className="font-semibold text-foreground-900">{c.userName}</span>
                    <span className="text-xs text-foreground-400">{c.time}</span>
                  </div>
                  <p className="mt-1.5 text-sm leading-relaxed text-foreground-700">{c.content}</p>
                  <div className="mt-2.5 flex items-center gap-4 text-xs text-foreground-500">
                    <button type="button" className="flex items-center gap-1 hover:text-primary-700">
                      <i className="ri-heart-3-line" aria-hidden="true"></i> {c.likes}
                    </button>
                    <button type="button" className="flex items-center gap-1 hover:text-primary-700">
                      <i className="ri-reply-line" aria-hidden="true"></i> 回复 {c.replies}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* 相关漫画推荐 */}
        <section className="mt-12" aria-label="相关漫画">
          <h2 className="flex items-center gap-2 font-heading text-xl font-black text-foreground-950">
            <i className="ri-book-2-line text-primary-600" aria-hidden="true"></i> 同类型漫画
          </h2>
          <div className="mt-5 flex flex-wrap gap-2">
            {comics.filter((c) => c.categoryId === article.categoryId).slice(0, 6).map((comic) => (
              <Link key={comic.id} to={`/category/${comic.categoryId}`} className="whitespace-nowrap rounded-full border border-background-300 px-4 py-1.5 text-sm text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-700">
                {comic.title}
              </Link>
            ))}
          </div>
        </section>
      </article>
    </>
  );
}